// SCR-006 (billing periods, its Close dialog and every refusal state) and the ledger panel of
// SCR-007 (API-020, API-021, API-022). Customer price, margin and the `not_billed` label need the
// customer invoice (ENT-026, TASK-121's screens/invoice); this table shows only what the ledger
// itself answers (ADR-020) — see the README follow-up for how the two are meant to meet.
import type { components } from "../../api/schema";
import type { ApiError } from "@nexus/ui";
import { forceReason } from "@nexus/ui";
import { MONTH_SHORT_NAMES } from "@nexus/ui";

export type BillingPeriod = components["schemas"]["BillingPeriod"];
export type BillingPeriodStatus = components["schemas"]["BillingPeriodStatus"];
export type ClosePeriodRequest = components["schemas"]["ClosePeriodRequest"];
export type Invoice = components["schemas"]["Invoice"];
export type InvoiceLine = components["schemas"]["InvoiceLine"];
export type Discrepancy = components["schemas"]["Discrepancy"];

export interface PeriodsFilters {
  period: string;
  status: "" | BillingPeriodStatus;
}

/** The current UTC month as `YYYY-MM` — the table's default filter. */
export function currentMonth(now: Date): string {
  return now.toISOString().slice(0, 7);
}

export function defaultFilters(now: Date): PeriodsFilters {
  return { period: currentMonth(now), status: "" };
}

/** Query parameters for `listAllBillingPeriods`; a blank status means every status. */
export function periodsQuery(filters: PeriodsFilters): { period: string; status?: BillingPeriodStatus } {
  return filters.status === "" ? { period: filters.period } : { period: filters.period, status: filters.status };
}

export const STATUS_OPTIONS: readonly { value: "" | BillingPeriodStatus; label: string }[] = [
  { value: "", label: "All" },
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
];

export type PeriodTone = "blue" | "amber" | "grey";

export interface PeriodStatusView {
  label: string;
  tone: PeriodTone;
}

/**
 * The status chip (redesign rules): the current month is open and still counting (blue); an open
 * month that has ended is waiting to be closed (amber); a closed month is grey, with "forced" when
 * the close overrode unverified or missing prices.
 */
export function periodStatusView(period: Pick<BillingPeriod, "status" | "period" | "forced">, now: Date): PeriodStatusView {
  if (period.status === "closed") return { label: period.forced ? "Closed (forced)" : "Closed", tone: "grey" };
  if (period.period >= currentMonth(now)) return { label: "Open, month to date", tone: "blue" };
  return { label: "Open, ready to close", tone: "amber" };
}

/** An API timestamp as a UTC day for a table cell: "3 Sep 2026"; the input as-is when it isn't one. */
export function formatDay(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return iso;
  return `${Number(match[3])} ${MONTH_SHORT_NAMES[Number(match[2]) - 1] ?? match[2]} ${match[1]}`;
}

/** Unmetered plus partially metered requests; null while the month has no counters yet. */
export function notFullyMetered(period: Pick<BillingPeriod, "unmeteredRequests" | "partiallyMeteredRequests">): number | null {
  if (period.unmeteredRequests === null && period.partiallyMeteredRequests === null) return null;
  return (period.unmeteredRequests ?? 0) + (period.partiallyMeteredRequests ?? 0);
}

/** The customer's invoice for the month (the Customers area's route). */
export function invoiceHref(period: Pick<BillingPeriod, "orgId" | "period">): string {
  return `/customers/${encodeURIComponent(period.orgId)}/invoices/${encodeURIComponent(period.period)}`;
}

/** The customer's Billing tab. */
export function customerBillingHref(orgId: string): string {
  return `/customers/${encodeURIComponent(orgId)}/billing`;
}

/** Sorts rows by the customer's name (then id), as the table shows them. */
export function comparePeriodsByName(nameOf: (orgId: string) => string): (a: BillingPeriod, b: BillingPeriod) => number {
  return (a, b) => nameOf(a.orgId).localeCompare(nameOf(b.orgId)) || a.orgId.localeCompare(b.orgId);
}

/** Nullable counts and amounts sort with the missing ones first. */
export function compareNullable(a: number | null, b: number | null): number {
  const x = a ?? -Infinity;
  const y = b ?? -Infinity;
  return x === y ? 0 : x < y ? -1 : 1;
}

/** Only an `open` period can be closed; a `closed` one answers idempotently but the row hides the action. */
export function canClosePeriod(period: BillingPeriod): boolean {
  return period.status === "open";
}

// ---- Close dialog (SCR-006) ----

export interface CloseDialogState {
  readonly open: boolean;
  readonly orgId: string;
  readonly period: string;
  readonly force: boolean;
  readonly reason: string;
  readonly busy: boolean;
  readonly error: ApiError | null;
}

export const CLOSE_DIALOG_CLOSED: CloseDialogState = {
  open: false,
  orgId: "",
  period: "",
  force: false,
  reason: "",
  busy: false,
  error: null,
};

export function openCloseDialog(orgId: string, period: string): CloseDialogState {
  return { open: true, orgId, period, force: false, reason: "", busy: false, error: null };
}

export function setForce(state: CloseDialogState, force: boolean): CloseDialogState {
  return { ...state, force };
}

export function setReason(state: CloseDialogState, reason: string): CloseDialogState {
  return { ...state, reason };
}

export function startClosing(state: CloseDialogState): CloseDialogState {
  return { ...state, busy: true, error: null };
}

export function closeFailed(state: CloseDialogState, error: ApiError): CloseDialogState {
  return { ...state, busy: false, error };
}

/** `null` while `force` is off; the reason's own problem (10–500 chars, SCR-006) while it is on. */
export function reasonProblem(state: CloseDialogState): string | null {
  return state.force ? forceReason(state.reason) : null;
}

export function canConfirmClose(state: CloseDialogState): boolean {
  return !state.busy && reasonProblem(state) === null;
}

export function closeRequestBody(state: CloseDialogState): ClosePeriodRequest {
  return state.force ? { force: true, reason: state.reason.trim() } : { force: false };
}

export type CloseRefusalCode =
  | "rollups_pending"
  | "unverified_prices"
  | "unpriced_models"
  | "period_not_ended"
  | "force_reason_required"
  // Revenue-plan additions (a billed org's customer invoice closes in month order, TASK-125):
  // an earlier month of the org still open, or closed with its own customer invoice still
  // pending final expense — force does not waive either; the earlier month closes/finalizes first.
  | "period_open"
  | "cur_not_final";

export interface CloseRefusal {
  code: string;
  message: string;
  /** Row-level or context detail the API named: the oldest pending hour, price rows, model ids. */
  details: readonly string[];
  /** Whether ticking "force" (with a reason) can get past this refusal (API-021). */
  forcible: boolean;
  retryable: boolean;
}

/** What each refusal means and what to do, in plain words, under the API's own message. */
export function refusalAdvice(code: string): string | null {
  switch (code) {
    case "period_not_ended":
      return "The month has not ended yet (UTC). Close it once it is over.";
    case "rollups_pending":
      return "Usage for the month is still being counted. This clears on its own; try again in a few minutes.";
    case "unverified_prices":
      return "Some usage is priced at a model price that is not verified. Verify the price in Model prices, or close anyway with a reason.";
    case "unpriced_models":
      return "A model used this month has no price. Add its price in Model prices, or close anyway with a reason.";
    case "force_reason_required":
      return "Closing anyway needs a reason of 10 to 500 characters.";
    case "period_open":
      return "An earlier month of this customer is still open. Months close in order: close that one first.";
    case "cur_not_final":
      return "An earlier month is waiting for its AWS bill to be finalized (actual-bill pricing). Finalize it in AWS costs first.";
    case "rate_plan_required":
      return "The customer is billed this month but has no rate plan. Assign one on the customer's Billing tab.";
    default:
      return null;
  }
}

const FORCIBLE_CODES: ReadonlySet<string> = new Set<CloseRefusalCode>(["unverified_prices", "unpriced_models"]);

/** Turns a `closeBillingPeriod` failure into what the dialog shows (SCR-006 "refusals rendered with their codes"). */
export function closeRefusalFrom(error: ApiError): CloseRefusal {
  return {
    code: error.code,
    message: error.message,
    details: error.details.map((detail) => detail.message ?? detail.field ?? detail.code),
    forcible: FORCIBLE_CODES.has(error.code),
    retryable: error.retryable,
  };
}

// ---- Ledger invoice (the ledger panel of SCR-007, API-022) ----

export interface LedgerInvoiceView {
  final: boolean;
  badge: "Final" | "Preview";
  totalMicroUsd: number | null;
  usageMicroUsd: number | null;
  adjustmentsMicroUsd: number | null;
  forced: boolean;
  forceReason: string | null;
  lines: readonly InvoiceLine[];
  discrepancies: readonly Discrepancy[];
  unpricedModels: readonly string[];
}

export function ledgerInvoiceView(invoice: Invoice): LedgerInvoiceView {
  return {
    final: invoice.final,
    badge: invoice.final ? "Final" : "Preview",
    totalMicroUsd: invoice.totalMicroUsd,
    usageMicroUsd: invoice.usageMicroUsd,
    adjustmentsMicroUsd: invoice.adjustmentsMicroUsd,
    forced: invoice.forced,
    forceReason: invoice.forceReason,
    lines: invoice.lines,
    discrepancies: invoice.discrepancies,
    unpricedModels: invoice.unpricedModels,
  };
}
